<template>
    <div v-if="componentLoaded" class="page-content">
        <div class="review-order__content container">
            <div class="section">
                <h1 class="title is-3 has-text-grey">
                    {{
                        orderTrackingInfo.billingInformation.paymentMethod ===
                        'quote'
                            ? $t('orderDetails.quote')
                            : $t('orderDetails.header')
                    }}}
                </h1>
            </div>
            <hr class="has-background-grey" />
            <div class="section">
                <h3 class="title is-4 has-text-grey mb-1">
                    {{ $t('orderDetails.subHeader') }}
                </h3>
                <div class="columns is-gapless">
                    <div class="column is-half-tablet summary-details">
                        <div class="detail__list">
                            <div
                                v-for="detailEntry in orderDetails"
                                :key="`order-detail__${detailEntry.label}`"
                                class="detail__entry order-sum__entry"
                            >
                                <div class="entry__label">
                                    {{ detailEntry.label }}
                                </div>
                                <div class="entry__value">
                                    {{ detailEntry.value }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="section">
                <div class="columns is-gapless">
                    <div class="column">
                        <hr class="has-background-grey" />
                        <h3
                            class="detail__header title is-4 is-uppercase has-text-grey"
                        >
                            Lieferadresse
                        </h3>
                        <div class="summary-details">
                            <div class="detail__list">
                                <div
                                    v-for="detailEntry in orderShippingAddress"
                                    :key="`order-shipping-detail__${detailEntry.label}`"
                                    class="detail__entry order-sum__entry"
                                >
                                    <div class="entry__label">
                                        {{ detailEntry.label }}
                                    </div>
                                    <div class="entry__value">
                                        {{ detailEntry.value }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="column">
                        <hr class="has-background-grey" />
                        <h3
                            class="detail__header title is-4 is-uppercase has-text-grey"
                        >
                            Rechnungsadresse
                        </h3>
                        <div class="summary-details">
                            <div class="detail__list">
                                <div
                                    v-for="detailEntry in orderBillingAddress"
                                    :key="`order-billing-detail__${detailEntry.label}`"
                                    class="detail__entry order-sum__entry"
                                >
                                    <div class="entry__label">
                                        {{ detailEntry.label }}
                                    </div>
                                    <div class="entry__value">
                                        {{ detailEntry.value }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hr class="has-background-grey" />
            <div class="section">
                <div class="columns">
                    <div class="column is-half-tablet">
                        <div class="order-sum summary-details">
                            <h3 class="title is-4 has-text-grey">
                                ZUSAMMENFASSUNG
                            </h3>
                            <div class="detail__list">
                                <div class="detail__entry">
                                    <span class="order-sum__label">{{
                                        orderPaymentDetails.paymentMethod.label
                                    }}</span
                                    ><span class="order-sum__value">{{
                                        orderPaymentDetails.paymentMethod.value
                                    }}</span>
                                </div>
                                <div class="detail__entry">
                                    <span class="order-sum__label">{{
                                        orderDetails.productionTime.label
                                    }}</span
                                    ><span class="order-sum__value">{{
                                        orderDetails.productionTime.value
                                    }}</span>
                                </div>
                                <div class="detail__entry">
                                    <span class="order-sum__label"
                                        >Voraussichtl. Versandzeit</span
                                    ><span class="order-sum__value">{{
                                        orderTrackingInfo.shippingMethod
                                            .shippingTimeDays
                                    }}</span>
                                </div>
                                <div class="detail__entry">
                                    <span class="order-sum__label"
                                        >Voraussichtl. Gesamtlieferzeit</span
                                    ><strong class="order-sum__value"
                                        >{{
                                            orderTrackingInfo.shippingMethod
                                                .shippingTimeDays +
                                            orderTrackingInfo.estimatedProductionTimeDays
                                        }}
                                        {{
                                            $t(
                                                'shippingTimeDays',
                                                orderTrackingInfo.shippingMethod
                                                    .shippingTimeDays +
                                                    orderTrackingInfo.estimatedProductionTimeDays
                                            )
                                        }}</strong
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="section">
                <div class="order-sum summary-details">
                    <div class="detail__list">
                        <div class="detail__entry order-sum__order-sum">
                            <div class="order-sum__label">
                                {{ orderPaymentDetails.orderSum.label }}
                            </div>
                            <div class="order-sum__value">
                                {{
                                    orderPaymentDetails.orderSum.value
                                        | toCents
                                        | currency
                                }}
                            </div>
                        </div>
                        <div class="detail__entry order-sum__order-sum">
                            <div class="order-sum__label">
                                {{ orderPaymentDetails.shippingPrice.label }}
                            </div>
                            <div class="order-sum__value">
                                {{ orderPaymentDetails.shippingPrice.value }}
                            </div>
                        </div>
                        <div
                            class="detail__entry order-sum__entry--primary has-background-primary-dark has-text-light section mt-2"
                        >
                            <div class="order-sum__label">
                                <span class="title is-4 has-text-white">{{
                                    orderPaymentDetails.sum.label
                                }}</span>
                                <span>{{
                                    $t('checkout.cart.includingVAT')
                                }}</span>
                            </div>
                            <strong
                                class="order-sum__value title is-4 has-text-white"
                            >
                                {{ orderPaymentDetails.sum.value }}€
                            </strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CartItemList from './CartItemList';
import CartSum from './ReviewOrder/CartSum';
import CustomerSummary from './ReviewOrder/CustomerSummary';
import ChooseShippingOption from './ReviewOrder/ChooseShippingOption';
import OrderSummary from './ReviewOrder/OrderSummary';
import OrderSum from './ReviewOrder/OrderSum';
import { mapGetters } from 'vuex';
import AddressSummary from './ReviewOrder/AdressSummary';

export default {
    name: 'TrackOrder',
    components: {
        AddressSummary,
        OrderSum,
        OrderSummary,
        ChooseShippingOption,
        CustomerSummary,
        CartSum,
        CartItemList,
    },
    data() {
        return {
            agreeToTermsAndConditions: false,
            orderIsProcessing: false,
            componentLoaded: false,
        };
    },
    computed: {
        ...mapGetters(['orderTrackingInfo']),
        orderDetails() {
            return {
                orderID: {
                    label: 'Auftragsnummer',
                    value: this.orderTrackingInfo.id,
                },
                orderDate: {
                    label: 'Bestelldatum',
                    value: this.orderTrackingInfo.date,
                },
                orderState: {
                    label: 'Bestellstatus',
                    value: this.orderTrackingInfo.publicStatus_de,
                },
                productionTime: {
                    label: 'Produktionszeit',
                    value: `${this.orderTrackingInfo.prodTime} ${
                        this.orderTrackingInfo.prodTime === 1
                            ? 'Arbeitstag'
                            : 'Arbeitstage'
                    }`,
                },
                shippingTime: {
                    label: '+ voraussichtlich Versandzeit',
                    value: `${this.orderTrackingInfo.shipTime} ${
                        this.orderTrackingInfo.shipTime === 1
                            ? 'Arbeitstag'
                            : 'Arbeitstage'
                    }`,
                },
            };
        },
        orderShippingAddress() {
            return {
                customerName: {
                    label: 'Name',
                    value: `${this.orderTrackingInfo.shipFirstName} ${this.orderTrackingInfo.shipLastName}`,
                },
                customerEmail: {
                    label: 'E-Mail',
                    value: this.orderTrackingInfo.eMail,
                },
                customerStreet: {
                    label: 'Adresse',
                    value: `${this.orderTrackingInfo.shipStreet} ${this.orderTrackingInfo.shipHouseNumber}`,
                },
                customerZipAndCity: {
                    label: 'Ort',
                    value: `${this.orderTrackingInfo.shipPostcode} ${this.orderTrackingInfo.shipCity}`,
                },
                customerCountry: {
                    label: 'Land',
                    value: `${this.orderTrackingInfo.shipCountry}`,
                },
            };
        },
        orderBillingAddress() {
            return {
                customerName: {
                    label: 'Name',
                    value: `${this.orderTrackingInfo.billFirstName} ${this.orderTrackingInfo.billLastName}`,
                },
                customerStreet: {
                    label: 'Addresse',
                    value: `${this.orderTrackingInfo.billStreet} ${this.orderTrackingInfo.billHouseNumber}`,
                },
                customerZipAndCity: {
                    label: 'Ort',
                    value: `${this.orderTrackingInfo.billPostcode} ${this.orderTrackingInfo.billCity}`,
                },
                customerCountry: {
                    label: 'Land',
                    value: `${this.orderTrackingInfo.billCountry}`,
                },
            };
        },
        orderPaymentDetails() {
            return {
                orderSum: {
                    label: 'Gesamtpreis (ohne Versand)',
                    value: `${(
                        parseFloat(this.orderTrackingInfo.priceBrutto) -
                        parseFloat(this.orderTrackingInfo.shipCost)
                    ).toFixed(2)}`,
                },
                shippingPrice: {
                    label: 'Versandkosten',
                    value: `${this.orderTrackingInfo.shipCost}`,
                },
                sum: {
                    label: 'Zahlungsbetrag Gesamt',
                    value: `${parseFloat(
                        this.orderTrackingInfo.priceBrutto
                    ).toFixed(2)}`,
                },
                paymentMethod: {
                    label: 'Bezahlung',
                    value: `${this.orderTrackingInfo.methodForm_de}`,
                },
            };
        },
    },
    methods: {
        fetchCMSData() {
            this.$store
                .dispatch('fetchCMSPage', { urlSlug: '/' })
                .then(() => (this.componentLoaded = true))
                .catch((error) => console.log(error));
        },
    },
    created() {
        this.fetchCMSData();

        this.$store.dispatch('fetchOrderTrackingInfo', {
            path: '/rest_api/trackorder.php',
            params: this.$route.query,
        });
    },
    mounted() {
        //this.renderPaypalButton();
    },
};
</script>

<style lang="scss">
#paypal-button-container {
    min-width: 300px;

    &.is-disabled {
        position: relative;
        opacity: 0.3;

        &:after {
            content: '';
            background-color: transparentize($grey-darker, 0.7);
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 10000;
            cursor: not-allowed;
        }
    }
}
</style>
